import type { EntitlementSnapshot, EntitlementState } from './subscriptions';

export interface EntitlementCaps {
  canCreateFamily: boolean;
  canInviteMember: boolean;
  canUseRecovery: boolean;
  canAccessSharedItems: boolean;
  isLapsedMember: boolean;
}

const PAID_OR_TRIAL: ReadonlySet<EntitlementState> = new Set([
  'trial',
  'active-subscriber',
  'active-member',
]);
const TRIAL_OR_SUBSCRIBER: ReadonlySet<EntitlementState> = new Set([
  'trial',
  'active-subscriber',
]);

/**
 * Pure derivation from the entitlement snapshot to the set of UI capability
 * booleans the clients gate on. Mirrors server/lib/entitlement.ts — drift is
 * a bug (the comment was load-bearing in PR #251).
 *
 * canCreateFamily:
 *   - trial OR hasUnusedActiveSub (pending-claim escape hatch).
 *   - active-subscriber passes when their seat pool has room (poolHasRoom):
 *     multiple owned subs share one pool, so an active-subscriber with an
 *     unused seat can create another family without buying a new sub. A
 *     full pool (or, historically, no unused sub at all) blocks it. Note
 *     poolHasRoom treats an ABSENT pool as unbounded (back-compat for
 *     flag-off / pre-pool snapshots) — this is only a client-side UI hint;
 *     the server gate (server/lib/entitlement.ts) still denies
 *     active-subscriber with no seat pool at all.
 */
export function deriveEntitlementCaps(
  snapshot: EntitlementSnapshot | null,
): EntitlementCaps {
  if (!snapshot) {
    return {
      canCreateFamily: false,
      canInviteMember: false,
      canUseRecovery: false,
      canAccessSharedItems: false,
      isLapsedMember: false,
    };
  }
  // Pending-claim escape hatch: a user who subscribed AFTER trial expired
  // sits in state='free' with linkState='unused' until they create a family
  // (which triggers claimUnusedSub server-side to link the sub). They need
  // create-family and recovery access to escape this pending state.
  // Mirrors server/lib/entitlement.ts gate logic exactly.
  const pendingClaim = snapshot.hasUnusedActiveSub === true;
  // Phase 2: invite capacity respects the seat pool when present. Absent
  // pool treated as unbounded for back-compat with flag-off (synthetic
  // snapshot) and Phase 1 snapshots that pre-date the pool field.
  const poolHasRoom =
    snapshot.seatPool === undefined ||
    snapshot.seatPool.used < snapshot.seatPool.capacity;
  return {
    canCreateFamily:
      snapshot.state === 'trial' ||
      pendingClaim ||
      (snapshot.state === 'active-subscriber' && poolHasRoom),
    canInviteMember: TRIAL_OR_SUBSCRIBER.has(snapshot.state) && poolHasRoom,
    canUseRecovery: PAID_OR_TRIAL.has(snapshot.state) || pendingClaim,
    canAccessSharedItems: PAID_OR_TRIAL.has(snapshot.state),
    isLapsedMember: snapshot.state === 'lapsed-member',
  };
}
